import type { ChatMessage } from '../types/envelope';
import type { ReportListItem } from '../types/report';

export interface ChatBubble {
  role: 'user' | 'assistant' | 'error';
  content: string;
  envelopeType?: 'text' | 'chart' | 'table';
  /** Single-chart payload (used when `charts` is absent). */
  data?: unknown;
  /** Multi-chart payload — each entry renders as its own chart-renderer. */
  charts?: Record<string, unknown>[];
}

export class ChatStore extends EventTarget {
  private _messages: ChatBubble[] = [];
  private _suggestions: string[] = [];
  private _isLoading = false;
  private _currentReport: ReportListItem | null = null;

  get messages(): readonly ChatBubble[] {
    return this._messages;
  }

  get suggestions(): readonly string[] {
    return this._suggestions;
  }

  get isLoading(): boolean {
    return this._isLoading;
  }

  get currentReport(): ReportListItem | null {
    return this._currentReport;
  }

  private emit(): void {
    this.dispatchEvent(new Event('change'));
  }

  addBubble(bubble: ChatBubble): void {
    this._messages = [...this._messages, bubble];
    this.emit();
  }

  setLoading(value: boolean): void {
    this._isLoading = value;
    this.emit();
  }

  setSuggestions(value: string[]): void {
    this._suggestions = value;
    this.emit();
  }

  clear(): void {
    this._messages = [];
    this._suggestions = [];
    this._isLoading = false;
    this.emit();
  }

  setCurrentReport(report: ReportListItem | null): void {
    this._currentReport = report;
    this._messages = [];
    this._suggestions = [];
    this.emit();
  }

  toChatHistory(): ChatMessage[] {
    return this._messages
      .filter((m) => m.role === 'user' || m.role === 'assistant')
      .map((m) => ({
        role: m.role as 'user' | 'assistant',
        content: m.content,
      }));
  }
}
